import {
    Avatar,
    Box,
    Flex,
    Icon,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Spinner,
    Text,
    useColorModeValue,
    useToast,
} from '@chakra-ui/react';
import React, {useEffect, useRef} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {logout} from '../../store/slices/userSlice.ts';
import {MdNotificationsNone} from 'react-icons/md';

import {
    useGetNotificationsQuery,
    useMarkAllSeenMutation,
    useMarkNotificationAsReadMutation,
} from '../../store/patientApi/patientApiSlice.ts';
import {SearchBar} from "./searchBar/SearchBar";
import {ItemContent} from "../menu/ItemContent";
import {SidebarResponsive} from "../sidebar/Sidebar";
import {RootState} from "../../store/store.ts";

export default function HeaderLinks() {
    const toast = useToast()
    const location = useLocation();
    const currentPath = location.pathname;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {data, isLoading, isError} = useGetNotificationsQuery();
    const [markNotificationAsRead] = useMarkNotificationAsReadMutation();
    const [markAllSeen] = useMarkAllSeenMutation();

    const handleLogout = () => {
        dispatch(logout());
        navigate('/signin');
    };

    const navbarIcon = useColorModeValue('gray.400', 'white');
    const menuBg = useColorModeValue('white', 'navy.800');
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const shadow = useColorModeValue(
        '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
        '14px 17px 40px 4px rgba(112, 144, 176, 0.06)'
    );

    // Count unseen notifications
    const unseenNotifications = data?.notifications?.filter(n => !n.is_seen) || [];
    const unseenCount = unseenNotifications.length;

    const user = useSelector((state: RootState) => state?.user?.user)


    const hasShownToast = useRef(false);

    useEffect(() => {
        if (!isLoading && unseenCount > 0 && !hasShownToast.current) {
            toast({
                title: "You have unseen notifications.",
                status: "info",
                duration: 3000,
                isClosable: true,
                position: "top",
            });
            hasShownToast.current = true;
        }
    }, [unseenCount, isLoading, toast]);

    return (
        <Flex
            w={{sm: '100%', md: 'auto'}}
            alignItems="center"
            flexDirection="row"
            bg={menuBg}
            flexWrap={'unset'}
            p="10px"
            borderRadius="30px"
            boxShadow={shadow}
        >
            {currentPath.includes('/admin/dashboard') ? null : <SearchBar me="10px" borderRadius="30px"/>}
            <SidebarResponsive/>

            {/* ✅ Notifications Menu */}
            <Menu onOpen={() => markAllSeen()}>
                <MenuButton p="0px" marginRight='10px' paddingLeft='10px'>
                    <Box position="relative">
                        <Icon
                            mt="6px"
                            as={MdNotificationsNone}
                            color={navbarIcon}
                            w="22px"
                            h="22px"
                            me="10px"
                        />
                        {unseenCount > 0 && (
                            <Box
                                position="absolute"
                                top="-1"
                                right="0"
                                bg="blue.500"
                                color="white"
                                fontSize="10px"
                                w="20px" // width
                                h="20px" // height
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                borderRadius="full"
                            >
                                {unseenCount}
                            </Box>
                        )}

                    </Box>
                </MenuButton>
                <MenuList
                    boxShadow={shadow}
                    p="20px"
                    borderRadius="20px"
                    bg={menuBg}
                    border="none"
                    mt="22px"
                    me={{base: '30px', md: 'unset'}}
                    minW={{base: 'unset', md: '400px', xl: '450px'}}
                    maxW={{base: '360px', md: 'unset'}}
                >
                    <Flex w="100%" mb="20px">
                        <Text fontSize="md" fontWeight="600" color={textColor}>
                            Notifications
                        </Text>
                    </Flex>

                    <Flex flexDirection="column"
                          maxH="600px"
                          overflowY="auto">
                        {isLoading && <Spinner/>}
                        {isError && (
                            <Text color="red.400">Failed to load notifications</Text>
                        )}
                        {data?.notifications?.length === 0 && !isLoading && (
                            <Text>No notifications found</Text>
                        )}

                        {data?.notifications?.map((notification) => (
                            <MenuItem
                                key={notification.uid}
                                _hover={{bg: 'grey.100'}}
                                _focus={{bg: 'grey.500'}}
                                px="0"
                                borderRadius="8px"
                                mb="10px"
                                bg={notification.is_read ? 'gray.100' : '#F4F7FE'}
                                onClick={async () => {
                                    try {
                                        await markNotificationAsRead(notification.uid);
                                    } catch (err) {
                                        console.error('Failed to mark as read:', err);
                                    }
                                    navigate(`/patients/${notification.patient_uid}`);
                                }}
                            >
                                <ItemContent info={notification.title}/>
                            </MenuItem>
                        ))}
                    </Flex>
                </MenuList>
            </Menu>

            {/* ✅ Avatar & Logout */}
            <Menu>
                <MenuButton p="0px">
                    <Avatar
                        _hover={{cursor: 'pointer'}}
                        color="white"
                        name={(user?.firstName) + ' ' + (user?.lastName)}
                        bg="#11047A"
                        size="sm"
                        w="40px"
                        h="40px"
                    />
                </MenuButton>
                <MenuList
                    boxShadow={shadow}
                    p="0px"
                    mt="10px"
                    borderRadius="20px"
                    bg={menuBg}
                    border="none"
                >
                    <Flex w="100%" mb="0px">
                        <Text
                            ps="20px"
                            pt="16px"
                            pb="10px"
                            w="100%"
                            borderBottom="1px solid"
                            fontSize="sm"
                            fontWeight="700"
                            color={textColor}
                        >
                            👋 Hey, {user?.firstName}
                        </Text>
                    </Flex>
                    <Flex flexDirection="column" p="10px">
                        <MenuItem
                            onClick={handleLogout}
                            _hover={{bg: 'none'}}
                            _focus={{bg: 'none'}}
                            color="red.400"
                            borderRadius="8px"
                            px="14px"
                        >
                            <Text fontSize="sm">Log out</Text>
                        </MenuItem>
                    </Flex>
                </MenuList>
            </Menu>
        </Flex>
    );
}